import { memo, useCallback, useEffect, useState } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useTheme } from 'styled-components';
import { GooSpinner } from 'react-spinners-kit';
import { AxiosError } from 'axios';
import { CgPoll } from 'react-icons/cg';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useChannel, useEvent } from '@harelpls/use-pusher';

import { useBroadcastQuery, usePollQuery } from '@hooks';
import { RoomPollsResult } from '@common/types/api';
import { ChartPollEvent } from '@common/types/pusher/events/poll';
import { api } from '@services';

import * as S from './styles';
import { BaseInteractionProps } from '../../types';

type MutationFnVariables = Record<'pollId' | 'optionId', number>;

// eslint-disable-next-line radar/cognitive-complexity
const Poll = ({ active: isActive }: BaseInteractionProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: room } = useBroadcastQuery({
    select: ({ id, settings }) => ({
      id,
      isPollEnabled: !!settings.data.poll,
    }),
    notifyOnChangeProps: ['data'],
  });

  const pollsQuery = usePollQuery(room?.id);
  const refetchPolls = pollsQuery.refetch;

  const [isLocked, setIsLocked] = useState(false);
  const [resultIndex, setResultIndex] = useState(0);

  const { mutateAsync, isLoading: isMutating } = useMutation(
    ({ pollId, optionId }: MutationFnVariables) =>
      api.post(`/rooms/${room?.id}/polls`, {
        poll_id: pollId,
        option: optionId,
      }),
    {
      onMutate: () => {
        setIsLocked(true);
      },
      onSuccess: ({ data }) => {
        toast.success(data.message);
      },
      onError: (err) => {
        toast.error(
          (err as AxiosError).response?.data.message ||
            t('pages.broadcast.fail'),
        );
        setIsLocked(false);
      },
      onSettled: () => {
        setTimeout(async () => {
          setIsLocked(false);
          await queryClient.invalidateQueries(['poll', room?.id]);
        }, 500);
      },
    },
  );

  const hasNoQuizzes =
    !pollsQuery.data?.polls.length && !pollsQuery.data?.totalAnsweredPolls;

  const hasAllQuizzesBeenAnswered =
    !!pollsQuery.data?.totalAnsweredPolls && !pollsQuery.data?.polls.length;

  const handleQuizAnswer = useCallback(
    (pollId: number, optionId: number) => () => {
      if (isLocked) return;
      mutateAsync({ pollId, optionId });
    },
    [mutateAsync, isLocked],
  );

  // Real-time: escutar resultados parciais da enquete atual
  const answeredPolls = pollsQuery.data?.answeredPolls || [];
  const currentResultPoll = answeredPolls[resultIndex];
  const chartChannelName = currentResultPoll
    ? `chart-poll.${currentResultPoll.id}`
    : null;

  const chartChannel = useChannel(chartChannelName || undefined);

  const handleChartUpdate = useCallback(
    (response?: ChartPollEvent) => {
      if (!response?.result || !room?.id) return;

      queryClient.setQueryData<RoomPollsResult | undefined>(
        ['poll', room.id],
        (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            answeredPolls: prev.answeredPolls.map((poll) =>
              poll.id === response.data?.id
                ? { ...poll, results: response.result }
                : poll,
            ),
          };
        },
      );
    },
    [queryClient, room?.id],
  );

  useEvent(chartChannel, 'ChartPoll', handleChartUpdate);

  useEffect(() => {
    isActive && refetchPolls();
  }, [isActive, refetchPolls]);

  if (hasNoQuizzes) {
    return (
      <S.Container $hide={!room?.isPollEnabled} $disabled={!isActive}>
        <S.MessageContainer>
          <CgPoll size="3rem" />
          <p>{t('pages.broadcast.poll.noPoll')}</p>
        </S.MessageContainer>
      </S.Container>
    );
  }

  if (hasAllQuizzesBeenAnswered) {
    const completedPolls = pollsQuery.data?.answeredPolls || [];
    const hasMultiple = completedPolls.length > 1;
    const currentPoll = completedPolls[resultIndex];

    return completedPolls.length ? (
      <S.ResultsContainer $hide={!room?.isPollEnabled} $disabled={!isActive}>
        {currentPoll && (
          <S.PollWrapper key={currentPoll.id}>
            <h3>{currentPoll.description}</h3>

            <S.PollOptionsContainer>
              {currentPoll.results.map(({ id, option, porcentagem }) => (
                <S.PollResult key={id}>
                  <div>
                    <div style={{ width: `${porcentagem}%` }} />
                    {`${option}`}
                    <strong>{`${porcentagem}%`}</strong>
                  </div>
                </S.PollResult>
              ))}
            </S.PollOptionsContainer>
          </S.PollWrapper>
        )}

        {hasMultiple && (
          <S.ResultsNav>
            <S.ResultsNavButton
              type="button"
              disabled={resultIndex === 0}
              onClick={() => setResultIndex((i) => i - 1)}
            >
              <FiChevronLeft size={16} />
              Anterior
            </S.ResultsNavButton>
            <S.ResultsCounter>
              {resultIndex + 1} / {completedPolls.length}
            </S.ResultsCounter>
            <S.ResultsNavButton
              type="button"
              disabled={resultIndex === completedPolls.length - 1}
              onClick={() => setResultIndex((i) => i + 1)}
            >
              Próximo
              <FiChevronRight size={16} />
            </S.ResultsNavButton>
          </S.ResultsNav>
        )}
      </S.ResultsContainer>
    ) : (
      <S.Container $hide={!room?.isPollEnabled} $disabled={!isActive}>
        <S.MessageContainer>
          <CgPoll size="3rem" />
          <p>{t('pages.broadcast.poll.noPoll')}</p>
        </S.MessageContainer>
      </S.Container>
    );
  }

  return (
    <S.Container $hide={!room?.isPollEnabled} $disabled={!isActive}>
      {pollsQuery.data?.polls.slice(0, 1).map((poll) => (
        <S.PollWrapper key={poll.id}>
          <h3>
            {poll.description}

            {isMutating && (
              <GooSpinner
                size={1.5}
                sizeUnit="rem"
                color={theme.colors.brand.primary.default}
              />
            )}
          </h3>

          <S.PollOptionsContainer>
            {poll.options.data.map(({ id, option, picture }) => (
              <S.PollOption key={id} $locked={isLocked}>
                <button
                  type="button"
                  disabled={isLocked}
                  onClick={handleQuizAnswer(poll.id, id)}
                >
                  {picture && <img src={picture} alt={option} />}
                  {option}
                </button>
              </S.PollOption>
            ))}
          </S.PollOptionsContainer>
        </S.PollWrapper>
      ))}
    </S.Container>
  );
};

export default memo(Poll);
